import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { firestore } from "../../config/ConfigFirebase";
import { ENTITY_TYPE } from "../../constants";

// Configuración del módulo de ENTIDADES — la única fuente de verdad para qué
// documentos se le exigen a cada tipo de entidad, los sectores, el bucket donde
// se guarda la documentación y el interruptor del registro público.
//
// Vive en config/entities y la leen tanto el frontend (este servicio) como el
// backend (gfunctions/entities/_shared/entityConfig.js). Editarla es sólo de
// ADMIN; leerla la puede hacer cualquier gestor.
//
// Está fuera del módulo de Convenios a propósito: una entidad no le pertenece a
// Convenios. Hoy la consume ese módulo y mañana la consumirá Contratos, así que
// su configuración se edita desde la pantalla general (/config → Entidades).
//
// La forma es abierta a propósito, igual que en aspirantes: la app sólo lee los
// campos estructurales de cada documento (key, label, required, enabled,
// min/maxFiles, order, acceptedMime, entityTypes, hasExpiry, hasIssueDate); el resto
// (description, aiHints, keywords, extra{}) es pasarela para la clasificación
// automática que venga después.

const seedDoc = (over) => ({
  key: "",
  label: "",
  required: true,
  enabled: true,
  minFiles: 1,
  maxFiles: 1,
  order: 0,
  acceptedMime: ["application/pdf", "image/*"],
  // Qué tipos de entidad deben entregarlo. VACÍO = todas. La convención de
  // "vacío = el conjunto estándar" es la misma de requiredStage1For en
  // aspirantes, y evita que añadir un tipo de entidad nuevo deje su catálogo en
  // blanco.
  entityTypes: [],
  // Documentos con vigencia: la fecha la escribe el gestor al cargarlos, y
  // "vencido" se DERIVA de ella (nunca se guarda — ver entityLogic.isExpired).
  // expiryWarningDays es a cuántos días del vencimiento se empieza a avisar.
  hasExpiry: false,
  expiryWarningDays: 30,
  // Documentos cuya vigencia se deriva de su fecha de EXPEDICIÓN (cuándo lo
  // generó la entidad emisora), no de un "vigente hasta" declarado por quien
  // carga. Son dos hechos distintos y un documento puede exigir los dos.
  //
  // Quién aplica la regla NO es este módulo: el catálogo solo dice que la fecha
  // se captura. Que el RUT sirva 3 meses es del módulo de pagos
  // (RUT_VALIDITY_MONTHS), y por eso se guarda la expedición y no una vigencia
  // ya calculada: si mañana la regla cambia, se aplica sola sobre lo guardado.
  hasIssueDate: false,
  description: "",
  aiHints: "",
  keywords: [],
  extra: {},
  ...over,
});

// Semilla con la documentación que se le pide de verdad a una entidad en
// Colombia. Está partida por tipo porque los papeles no son los mismos: una
// alcaldía no tiene cámara de comercio y una empresa privada no tiene acto
// administrativo de creación.
export const DEFAULT_ENTITY_DOCUMENT_CATALOG = [
  seedDoc({
    key: "rut",
    label: "RUT (DIAN)",
    description: "Registro Único Tributario actualizado.",
    // El RUT no declara "vigente hasta": lo que importa es cuándo se expidió,
    // porque la solicitud de pago jurídica exige uno expedido dentro de los
    // últimos 3 meses (daily 2026-08-20). De ahí hasExpiry en false y
    // hasIssueDate en true.
    hasIssueDate: true,
    order: 10,
  }),
  seedDoc({
    key: "chamber_of_commerce",
    label: "Certificado de existencia y representación legal",
    description: "Expedido por la Cámara de Comercio.",
    entityTypes: [ENTITY_TYPE.PRIVATE, ENTITY_TYPE.MIXED, ENTITY_TYPE.NONPROFIT],
    hasExpiry: true,
    expiryWarningDays: 30,
    order: 20,
  }),
  seedDoc({
    key: "creation_act",
    label: "Acto administrativo de creación",
    description: "Ley, ordenanza, acuerdo o decreto que crea la entidad.",
    entityTypes: [ENTITY_TYPE.PUBLIC],
    order: 30,
  }),
  seedDoc({
    key: "legal_rep_appointment",
    label: "Acta de posesión o nombramiento del representante legal",
    entityTypes: [ENTITY_TYPE.PUBLIC, ENTITY_TYPE.MIXED],
    order: 40,
  }),
  seedDoc({
    key: "legal_rep_id",
    label: "Documento de identidad del representante legal",
    maxFiles: 2,
    order: 50,
  }),
  seedDoc({
    key: "bank_certificate",
    label: "Certificación bancaria",
    description: "Cuenta a nombre de la entidad, para los desembolsos.",
    hasExpiry: true,
    expiryWarningDays: 90,
    order: 60,
  }),
  seedDoc({
    key: "social_security",
    label: "Paz y salvo de seguridad social y parafiscales",
    entityTypes: [ENTITY_TYPE.PRIVATE, ENTITY_TYPE.MIXED, ENTITY_TYPE.NONPROFIT],
    hasExpiry: true,
    expiryWarningDays: 30,
    order: 70,
  }),
];

export const DEFAULT_SECTORS = [
  "Educación",
  "Salud",
  "Cultura",
  "Deporte y recreación",
  "Infraestructura",
  "Tecnología",
  "Medio ambiente",
  "Desarrollo social",
  "Desarrollo económico",
  "Seguridad y convivencia",
];

// Tipos de documento de identidad del REPRESENTANTE LEGAL. Configurable —y no un
// enum en constants.js— por lo mismo que en aspirantes: la lista cambia por
// política (un PEP, un PPT) y no debería costar un despliegue. Se guarda el
// texto largo, igual que applicants.idType.
export const DEFAULT_ID_TYPES = [
  "Cédula de ciudadanía",
  "Cédula de extranjería",
  "Pasaporte",
];

export const DEFAULT_ENTITY_CONFIG = {
  entityDocumentCatalog: DEFAULT_ENTITY_DOCUMENT_CATALOG,
  sectors: DEFAULT_SECTORS,
  idTypes: DEFAULT_ID_TYPES,
  // Dónde se guardan los documentos de las entidades. Cae al bucket por defecto
  // del proyecto si no hay uno propio, para que el módulo funcione sin tocar el
  // entorno; el backend lee el efectivo de aquí.
  storageBucket:
    process.env.REACT_APP_STORAGE_BUCKET_ENTITIES ||
    process.env.REACT_APP_STORAGE_BUCKET ||
    "",

  // --- Registro público de entidades ---
  // Interruptor del portal (/submission → «Empresas y entidades»). Apagarlo
  // esconde la pestaña y deja de aceptar registros nuevos, sin tocar las fichas
  // que ya existen ni impedir que sus dueños entren a actualizarlas.
  publicRegistrationEnabled: true,
  // Tope por archivo en el portal. El backend impone además su propio tope duro
  // de transporte (32 MB), que este número no puede superar en la práctica.
  entityMaxFileBytes: 10 * 1024 * 1024,
  // Modo de prueba del SMS: usa el código fijo de Firebase Phone Auth en vez de
  // gastar un mensaje real. Sólo para dev — en producción va apagado.
  smsTestMode: false,
};

// Claves que esta pantalla posee. Explícitas para que un guardado nunca pise
// campos ajenos, igual que GLOBAL_CONFIG_KEYS en AppConfigService.
export const ENTITY_CONFIG_KEYS = Object.keys(DEFAULT_ENTITY_CONFIG);

const pick = (obj, keys) => Object.fromEntries(keys.filter((k) => k in obj).map((k) => [k, obj[k]]));

// Banderas estructurales que se rellenan desde la semilla cuando la
// configuración guardada no las trae. Al agregar una bandera nueva al catálogo
// hay que sumarla aquí: sin esto, un entorno que ya guardó config/entities
// seguiría comportándose como antes hasta que un ADMIN vuelva a guardar la
// pantalla, y nadie relaciona "el RUT no pide la fecha" con eso.
const CATALOG_BACKFILL_FLAGS = ["hasIssueDate"];

// Solo cubre AUSENCIAS: un `false` guardado a propósito se respeta, y una
// entrada que el admin agregó y no está en la semilla no hereda nada.
// El backend hace exactamente lo mismo (gfunctions/entities/_shared/entityConfig.js).
const withCatalogDefaults = (list) => {
  if (!Array.isArray(list)) return list;
  return list.map((docDef) => {
    const seed = DEFAULT_ENTITY_DOCUMENT_CATALOG.find((s) => s.key === docDef?.key);
    if (!seed) return docDef;
    const filled = { ...docDef };
    CATALOG_BACKFILL_FLAGS.forEach((flag) => {
      if (!(flag in filled)) filled[flag] = seed[flag];
    });
    return filled;
  });
};

// Lee config/entities sobre los valores por defecto, de modo que quien lo
// consuma reciba siempre un objeto completo. Devuelve los defaults ante un
// error o si el documento no existe.
//
// COMPATIBILIDAD: esta configuración vivía dentro de config/agreements. Mientras
// un entorno no la haya guardado en su nuevo sitio, se sigue leyendo de allí
// para no dejar el catálogo en blanco. El primer guardado desde la pantalla
// escribe config/entities y esta rama deja de tomarse; se puede borrar cuando
// dev y prod estén migrados. El backend hace exactamente lo mismo.
export const getEntityConfig = async () => {
  try {
    let snap = await getDoc(doc(firestore, "config", "entities"));
    if (!snap.exists()) snap = await getDoc(doc(firestore, "config", "agreements"));
    const data = snap.exists() ? snap.data() : {};
    const merged = { ...DEFAULT_ENTITY_CONFIG, ...pick(data, ENTITY_CONFIG_KEYS) };
    return {
      ...merged,
      entityDocumentCatalog: withCatalogDefaults(merged.entityDocumentCatalog),
    };
  } catch (e) {
    console.error("getEntityConfig error:", e);
    return { ...DEFAULT_ENTITY_CONFIG };
  }
};

// Escritura con merge, acotada a las claves propias.
export const saveEntityConfig = async (patch) => {
  try {
    await setDoc(
      doc(firestore, "config", "entities"),
      { ...pick(patch, ENTITY_CONFIG_KEYS), updatedAt: serverTimestamp() },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("saveEntityConfig error:", e);
    return false;
  }
};

// --- Selectores (puros; los usan el formulario y la ficha de la entidad) -----

// El catálogo completo, ordenado (habilitados y no) — lo que ve el admin.
export const allEntityDocs = (cfg) =>
  (cfg?.entityDocumentCatalog || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

export const entityDocByKey = (cfg, key) =>
  (cfg?.entityDocumentCatalog || []).find((d) => d && d.key === key) || null;

export const entityDocLabel = (cfg, key) => entityDocByKey(cfg, key)?.label || key;

// 0 = ilimitado (Infinity); cualquier otra cosa cae a 1 como mínimo. Misma
// convención que maxFilesFor en aspirantes.
export const maxFilesForEntityDoc = (cfg, key) => {
  const n = Math.floor(Number(entityDocByKey(cfg, key)?.maxFiles));
  if (n === 0) return Infinity;
  return Number.isFinite(n) && n > 0 ? n : 1;
};

export const sectorOptions = (cfg) =>
  (cfg?.sectors || []).map((s) => String(s || "").trim()).filter(Boolean);

// Los tipos de documento del representante legal, ya limpios. Cae a los de
// fábrica si un admin dejó la lista vacía: un selector sin opciones haría
// imposible registrar al representante.
export const idTypeOptions = (cfg) => {
  const list = (cfg?.idTypes || []).map((s) => String(s || "").trim()).filter(Boolean);
  return list.length ? list : DEFAULT_ID_TYPES;
};
